import { ChaseParser } from './banks/ChaseParser';
import { AmexParser } from './banks/AmexParser';
import { USBankParser } from './banks/USBankParser';
import { SoFiParser } from './banks/SoFiParser';
import { BMOParser } from './banks/BMOParser';
import { BofAParser } from './banks/BofAParser';
import { BaseParser } from './BaseParser';
import type { ParsedTransaction, ParserInfo } from './BaseParser';

/**
 * Parser Registry State
 * Manages the list of registered parsers
 */
export type ParserRegistryState = {
  parsers: BaseParser[];
};

/**
 * Create a new parser registry state
 */
export function createParserRegistry(): ParserRegistryState {
  return {
    parsers: []
  };
}

/**
 * Create a parser registry with default parsers
 */
export function createDefaultParserRegistry(): ParserRegistryState {
  const state = createParserRegistry();
  registerDefaultParsers(state);
  return state;
}

/**
 * Register default parsers
 */
function registerDefaultParsers(state: ParserRegistryState): void {
  registerParser(state, new ChaseParser());
  registerParser(state, new AmexParser());
  registerParser(state, new USBankParser());
  registerParser(state, new SoFiParser());
  registerParser(state, new BMOParser());
  registerParser(state, new BofAParser());
}

/**
 * Register a new parser
 * @param state - Parser registry state
 * @param parser - Parser instance to register
 */
export function registerParser(state: ParserRegistryState, parser: BaseParser): void {
  if (!parser.detect || !parser.parse) {
    throw new Error('Parser must implement detect() and parse() methods');
  }

  state.parsers.push(parser);

  // Sort by priority (lower number = higher priority)
  state.parsers.sort((a, b) => a.priority - b.priority);
}

/**
 * Detect the best parser for the given CSV
 * @param state - Parser registry state
 * @param header - Lowercased header line
 * @param bank - Bank hint from folder structure
 * @returns Best matching parser or null
 */
export function detectParser(
  state: ParserRegistryState,
  header: string,
  bank: string | null
): BaseParser | null {
  let bestParser: BaseParser | null = null;
  let bestConfidence = 0;

  for (const parser of state.parsers) {
    const confidence = parser.detect(header, bank || '');

    if (confidence > bestConfidence) {
      bestConfidence = confidence;
      bestParser = parser;
    }
  }

  if (bestParser && bestConfidence > 0) {
    return bestParser;
  }

  return null;
}

/**
 * Extract bank name from filename path
 * @param filename - Full file path
 * @returns Bank name or null
 */
export function extractBankFromFilename(filename: string): string | null {
  const parts = filename.split('/');
  if (parts.length > 0) {
    return parts[0].toLowerCase();
  }
  return null;
}

/**
 * Parse CSV using auto-detected parser
 * @param state - Parser registry state
 * @param csvText - CSV content
 * @param filename - Source filename
 * @param bankHint - Optional bank hint
 * @returns Parsed transactions
 */
export function parseCSV(
  state: ParserRegistryState,
  csvText: string,
  filename: string,
  bankHint: string | null = null
): ParsedTransaction[] {
  const lines = csvText.trim().split('\n');
  if (lines.length < 2) {
    return [];
  }

  const header = lines[0].toLowerCase();

  // Extract bank from filename if not provided
  const bank = bankHint || extractBankFromFilename(filename);

  // Find the best parser
  const parser = detectParser(state, header, bank);

  if (!parser) {
    console.warn('[ParserRegistry] No parser found for:', header, 'bank:', bank);
    return [];
  }

  // Parse using the selected parser
  try {
    const transactions = parser.parse(lines, filename);
    console.log(`[ParserRegistry] Parsed ${transactions.length} transactions using ${parser.name}`);
    return transactions;
  } catch (error) {
    console.error(`[ParserRegistry] Error parsing with ${parser.name}:`, error);
    return [];
  }
}

/**
 * Get list of all registered parsers
 * @param state - Parser registry state
 * @returns Parser info objects
 */
export function getRegisteredParsers(state: ParserRegistryState): ParserInfo[] {
  return state.parsers.map(p => p.getInfo());
}

/**
 * Get parser by name
 * @param state - Parser registry state
 * @param name - Parser name
 * @returns Parser instance or null
 */
export function getParser(state: ParserRegistryState, name: string): BaseParser | null {
  return state.parsers.find(p => p.name === name) || null;
}

/**
 * Clear all parsers
 * @param state - Parser registry state
 */
export function clearParsers(state: ParserRegistryState): void {
  state.parsers = [];
  console.log('[ParserRegistry] Cleared all parsers');
}

/**
 * Get count of registered parsers
 * @param state - Parser registry state
 * @returns Number of registered parsers
 */
export function getParserCount(state: ParserRegistryState): number {
  return state.parsers.length;
}

// Module-level default registry instance for convenience
export const defaultParserRegistry = createDefaultParserRegistry();
